import { Autocomplete, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { saveVehicle } from '../../../../api/transports';
import { useSelector } from '../../../../redux/store';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { DATE_PICKER_SLOT_PROPS } from '../constants';
import FacilitySelector from '../Shared/FacilitySelector';
import EditModal from './EditModal';

VehicleModal.propTypes = {
  edit: PropTypes.bool,
  row: PropTypes.object,
  onAdded: PropTypes.func,
};

export default function VehicleModal({ edit, row, onAdded }) {
  const MIN_VEHICLE_YEAR = 2000;
  const MAX_VEHICLE_YEAR = new Date().getFullYear();
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState({
    id: null,
    vin: null,
    make: null,
    model: null,
    year: null,
    licensePlate: null,
    registrationExpiresAt: null,
    ownershipStatus: null,
    facilityId: null,
    deleteFlag: false,
  });
  const transportsState = useSelector((state) => state.transports);
  const facilities = transportsState?.data?.facilities ?? [];
  const facilityDataIsLoading = transportsState?.isLoading ?? false;
  const parsedYear = Number(value.year);
  const isYearValid = Number.isInteger(parsedYear) && parsedYear >= MIN_VEHICLE_YEAR && parsedYear <= MAX_VEHICLE_YEAR;
  const fieldSx = {
    mt: 1,
    '& legend': { display: 'none' },
    '& fieldset': { top: 0 },
    '& .MuiFormLabel-root ': { display: 'none' },
  };

  const handleSave = async () => {
    try {
      const response = await saveVehicle(value);
      const result = response.data;
      if (result.success) {
        setOpen(false);
        enqueueSnackbar(result.message || 'Vehicle updated successfully', { variant: 'success' });
        if (onAdded) onAdded();
      } else {
        enqueueSnackbar(result.message || 'Failed to update Vehicle', { variant: 'error' });
      }
    } catch (error) {
      // Show backend error if present
      const errorMsg = 'Failed to update Vehicle';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    }
  };

  const handleClickOpen = () => {
    setValue({
      id: edit ? row.id : null,
      vin: edit ? row.vin : null,
      make: edit ? row.make : null,
      model: edit ? row.model : null,
      year: edit ? row.year : null,
      licensePlate: edit ? row.licensePlate : null,
      registrationExpiresAt:
        edit && row.registrationExpiresAt
          ? row.registrationExpiresAt instanceof Date
            ? row.registrationExpiresAt
            : new Date(row.registrationExpiresAt)
          : null,
      ownershipStatus: edit ? row.ownershipStatus : null,
      facilityId: edit ? row.facilityId : null,
      deleteFlag: edit ? row.deleteFlag : false,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setValue({
      id: null,
      vin: null,
      make: null,
      model: null,
      year: null,
      licensePlate: null,
      registrationExpiresAt: null,
      ownershipStatus: null,
      facilityId: null,
      deleteFlag: false,
    });
    setOpen(false);
  };

  // Reusable text field generator
  const renderTextField = (fieldKey, label) => (
    <TextField
      fullWidth
      size="small"
      value={value[fieldKey] || ''}
      onChange={(e) => setValue({ ...value, [fieldKey]: e.target.value })}
      label={label}
      sx={fieldSx}
      autoComplete="off"
    />
  );

  const renderYearField = () => (
    <TextField
      fullWidth
      size="small"
      type="number"
      value={value.year || ''}
      onChange={(e) => {
        const nextValue = e.target.value;
        if (!/^\d{0,4}$/.test(nextValue)) {
          return;
        }
        setValue({ ...value, year: nextValue });
      }}
      label="Year"
      sx={fieldSx}
      inputProps={{ min: MIN_VEHICLE_YEAR, max: MAX_VEHICLE_YEAR, step: 1 }}
      error={Boolean(value.year) && !isYearValid}
      helperText={
        Boolean(value.year) && !isYearValid ? `Year must be between ${MIN_VEHICLE_YEAR} and ${MAX_VEHICLE_YEAR}` : ''
      }
      autoComplete="off"
    />
  );

  const fields = [
    { label: 'VIN', element: renderTextField('vin', 'VIN') },
    { label: 'Make', element: renderTextField('make', 'Make') },
    { label: 'Model', element: renderTextField('model', 'Model') },
    { label: 'Year', element: renderYearField() },
    { label: 'License Plate', element: renderTextField('licensePlate', 'License Plate') },
    {
      label: 'Registration Expires At',
      element: (
        <DatePicker
          disablePast
          label="Registration Expiration"
          slotProps={DATE_PICKER_SLOT_PROPS}
          value={value.registrationExpiresAt || null}
          onChange={(val) => {
            if (val) {
              const utcMidnight = new Date(Date.UTC(val.getFullYear(), val.getMonth(), val.getDate()));
              setValue({ ...value, registrationExpiresAt: utcMidnight });
            } else {
              setValue({ ...value, registrationExpiresAt: null });
            }
          }}
          inputProps={{ 'aria-label': 'Registration Expiration Datepicker' }}
        />
      ),
    },
    {
      label: 'Ownership Status',
      element: (
        <Autocomplete
          fullWidth
          size="small"
          value={value.ownershipStatus || null}
          onChange={(event, val) => setValue({ ...value, ownershipStatus: val })}
          options={['Owned', 'Leased', 'Rented']}
          renderInput={(params) => <TextField {...params} label="Select a status" autoComplete="off" />}
          inputProps={{ 'aria-label': 'Ownership Status' }}
        />
      ),
    },
    {
      label: 'Facility',
      element: (
        <FacilitySelector
          facilities={facilities}
          value={value.facilityId}
          onChange={(val) => setValue({ ...value, facilityId: val })}
          isLoading={facilityDataIsLoading}
          inputProps={{ 'aria-label': 'Facility Selector' }}
        />
      ),
    },
  ];

  return (
    <EditModal
      handleClickOpen={handleClickOpen}
      editEntity="Vehicle"
      handleClose={handleClose}
      handleSave={handleSave}
      disableSave={!isYearValid || !value.make || !value.model || !value.vin || !value.facilityId}
      fields={fields}
      edit={edit}
      open={open}
    />
  );
}
